import React, { useState, useEffect } from "react";
import { api, formatCurrency, formatDate, API_URL, getAuthHeaders } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { CalendarDays, Download, DollarSign, Wallet, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";

// Flat period-correct palette sourced from the reskinned --chart-1..5 tokens
// (navy / teal / maroon / olive / purple); the browser resolves the CSS var in SVG.
var CHART_COLORS = ["#000080", "#008080", "#800000", "#808000", "#800080"];

var PRESETS = {
  "7d": { label: "Last 7 Days", days: 7 },
  "30d": { label: "Last 30 Days", days: 30 },
  "90d": { label: "Last 90 Days", days: 90 },
  "year": { label: "Last Year", days: 365 },
};

function getDateRange(timeRange, customStart, customEnd) {
  var endDate = new Date().toISOString().split("T")[0];
  var startDate;

  if (timeRange === "custom" && customStart && customEnd) {
    startDate = customStart.toISOString().split("T")[0];
    endDate = customEnd.toISOString().split("T")[0];
  } else {
    var preset = PRESETS[timeRange];
    if (preset) {
      var d = new Date();
      d.setDate(d.getDate() - preset.days);
      startDate = d.toISOString().split("T")[0];
    } else {
      var d2 = new Date();
      d2.setDate(d2.getDate() - 30);
      startDate = d2.toISOString().split("T")[0];
    }
  }

  return { startDate: startDate, endDate: endDate };
}

export function ReportsPage() {
  var [timeRange, setTimeRange] = useState("30d");
  var [customStart, setCustomStart] = useState(null);
  var [customEnd, setCustomEnd] = useState(null);
  var [reportData, setReportData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [exporting, setExporting] = useState(false);

  useEffect(function() {
    fetchReport();
  }, [timeRange, customStart, customEnd]);

  function fetchReport() {
    var range = getDateRange(timeRange, customStart, customEnd);
    if (timeRange === "custom" && (!customStart || !customEnd)) return;

    setLoading(true);
    api.get("/reports/summary?start_date=" + range.startDate + "&end_date=" + range.endDate)
      .then(function(data) {
        setReportData(data);
      })
      .catch(function() {
        toast.error("Failed to load report data");
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleExportPDF() {
    var range = getDateRange(timeRange, customStart, customEnd);
    setExporting(true);

    fetch(API_URL + "/reports/expenses/pdf?start_date=" + range.startDate + "&end_date=" + range.endDate, {
      headers: getAuthHeaders()
    })
      .then(function(response) {
        if (!response.ok) throw new Error("Failed to generate PDF");
        return response.blob();
      })
      .then(function(blob) {
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = "expense_report_" + range.startDate + "_to_" + range.endDate + ".pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Expense report downloaded");
      })
      .catch(function(err) {
        toast.error(err.message);
      })
      .finally(function() {
        setExporting(false);
      });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 mx-auto animate-pulse-glow" />
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  var chartData = reportData?.chart_data || [];
  var categoryBreakdown = reportData?.category_breakdown || [];
  var totalRevenue = reportData?.total_revenue || 0;
  var totalExpenses = reportData?.total_expenses || 0;
  var profitLoss = reportData?.profit_loss || 0;
  var invoiceCount = reportData?.invoice_count || 0;
  var expenseCount = reportData?.expense_count || 0;
  var profitMargin = totalRevenue > 0 ? ((profitLoss / totalRevenue) * 100).toFixed(1) : "0.0";

  var sortedCategories = categoryBreakdown.slice().sort(function(a, b) { return b.value - a.value; });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading">
            <span className="gradient-text">Reports</span>
          </h1>
          <p className="text-muted-foreground mt-1">Financial overview and expense analysis</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={timeRange} onValueChange={function(val) { setTimeRange(val); }}>
            <SelectTrigger className="w-[160px] rounded-xl">
              <CalendarDays className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>

          {timeRange === "custom" && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="rounded-xl text-sm">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {customStart ? formatDate(customStart.toISOString()) : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-xl" align="start">
                  <Calendar
                    mode="single"
                    selected={customStart}
                    onSelect={setCustomStart}
                    disabled={function(date) { return date > new Date(); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="rounded-xl text-sm">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {customEnd ? formatDate(customEnd.toISOString()) : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-xl" align="start">
                  <Calendar
                    mode="single"
                    selected={customEnd}
                    onSelect={setCustomEnd}
                    disabled={function(date) { return date > new Date() || (customStart && date < customStart); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="rounded-3xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Gross Revenue</p>
                <p className="text-4xl font-bold font-mono text-primary mt-2">{formatCurrency(totalRevenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">{invoiceCount} paid invoice{invoiceCount !== 1 ? "s" : ""}</p>
              </div>
              <div className="p-3 rounded-2xl bg-primary/20">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Gross Expenses</p>
                <p className="text-4xl font-bold font-mono text-destructive mt-2">{formatCurrency(totalExpenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">{expenseCount} expense{expenseCount !== 1 ? "s" : ""}</p>
              </div>
              <div className="p-3 rounded-2xl bg-destructive/20">
                <Wallet className="w-6 h-6 text-destructive" />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 rounded-xl text-xs border-secondary/30 hover:bg-secondary/10"
              onClick={handleExportPDF}
              disabled={exporting || expenseCount === 0}
            >
              <Download className="w-3 h-3 mr-1" />
              {exporting ? "Exporting..." : "Export PDF"}
            </Button>
          </CardContent>
        </Card>

        <Card className={profitLoss >= 0
          ? "rounded-3xl bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20 hover:shadow-lg transition-shadow duration-300"
          : "rounded-3xl bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20 hover:shadow-lg transition-shadow duration-300"
        }>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Gross Profit</p>
                <p className={profitLoss >= 0
                  ? "text-4xl font-bold font-mono mt-2 text-green-500"
                  : "text-4xl font-bold font-mono mt-2 text-red-500"
                }>{formatCurrency(profitLoss)}</p>
                <p className="text-xs text-muted-foreground mt-1">{profitMargin}% margin</p>
              </div>
              <div className="p-3 win-well">
                {profitLoss >= 0
                  ? <TrendingUp className="w-6 h-6 text-green-500" />
                  : <TrendingDown className="w-6 h-6 text-red-500" />
                }
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue vs Expenses Chart */}
      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader className="pb-0">
          <CardTitle className="font-heading text-lg">Revenue vs Expenses Over Time</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {chartData.length > 0 ? (
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={4} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={function(v) { return "$" + v.toLocaleString(); }} />
                  <Tooltip
                    formatter={function(value) { return formatCurrency(value); }}
                    cursor={{ fill: "hsl(var(--muted-foreground))", fillOpacity: 0.15 }}
                    contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0" }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#000080" />
                  <Bar dataKey="expenses" name="Expenses" fill="#800000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-12">No data for the selected period</p>
          )}
        </CardContent>
      </Card>

      {/* Expense Breakdown — Pie Chart + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-heading text-lg">Expense Categories</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {sortedCategories.length > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sortedCategories}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {sortedCategories.map(function(entry, index) {
                        return <Cell key={"cell-" + index} fill={CHART_COLORS[index % CHART_COLORS.length]} />;
                      })}
                    </Pie>
                    <Tooltip
                      formatter={function(value) { return formatCurrency(value); }}
                      contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-3 -mt-4">
                  {sortedCategories.map(function(cat, index) {
                    return (
                      <div key={cat.name} className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                        <span className="text-muted-foreground">{cat.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-12">No expenses in this period</p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
          <CardHeader className="pb-0">
            <CardTitle className="font-heading text-lg">Category Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {sortedCategories.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCategories.map(function(cat, index) {
                    var pct = totalExpenses > 0 ? ((cat.value / totalExpenses) * 100).toFixed(1) : "0.0";
                    return (
                      <TableRow key={cat.name} className="hover:bg-accent/30">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                            <span className="font-medium">{cat.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(cat.value)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{pct}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="border-t-2 font-bold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono text-destructive">{formatCurrency(totalExpenses)}</TableCell>
                    <TableCell className="text-right">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-center py-12">No expenses in this period</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Summary */}
      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader className="pb-0">
          <CardTitle className="font-heading text-lg">Revenue Summary</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-2xl bg-accent/20">
              <p className="text-sm text-muted-foreground">Paid Invoices</p>
              <p className="text-2xl font-bold font-mono text-primary mt-1">{invoiceCount}</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-accent/20">
              <p className="text-sm text-muted-foreground">Average Invoice</p>
              <p className="text-2xl font-bold font-mono text-primary mt-1">
                {formatCurrency(invoiceCount > 0 ? totalRevenue / invoiceCount : 0)}
              </p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-accent/20">
              <p className="text-sm text-muted-foreground">Average Expense</p>
              <p className="text-2xl font-bold font-mono text-destructive mt-1">
                {formatCurrency(expenseCount > 0 ? totalExpenses / expenseCount : 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
